import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'store';
import { reduxActions, useDispatch } from 'store';

const { REACT_APP_IS_CHAT_WIDGET_VISIBLE } = process.env;
const THREAD_ID_POLLING_INTERVAL = 500; // ms

export type ChatMode = 'docked' | 'horizontal' | 'floating' | 'fullscreen';

export interface UseChatStateOptions {
  /**
   * Czy hook powinien zarządzać pozycją okna chata (dla ChatLauncher)
   */
  manageWindowPosition?: boolean;
  /**
   * Czy hook powinien obsługiwać keyboard shortcuts (Ctrl+A)
   */
  enableKeyboardShortcuts?: boolean;
}

export interface ChatWindowPosition {
  top: number;
  left: number;
}

export interface UseChatStateReturn {
  // Stan z Redux
  isAgentAiVisible: boolean;
  isChatOpen: boolean;
  isChatDocked: boolean;
  isChatHorizontal: boolean;
  isChatResizable: boolean;
  isChatFullScreen: boolean;

  // Stan pozycji okna (tylko gdy manageWindowPosition = true)
  chatWindowPosition: ChatWindowPosition | null;
  dragResetKey: number;

  // Funkcje pomocnicze
  shouldShowAgentAI: (mode?: 'docked' | 'horizontal') => boolean;

  // Funkcje zarządzania stanem
  closeChat: () => void;
  toggleChat: () => void;
  toggleDock: () => void;
  toggleHorizontal: () => void;
  toggleResizable: () => void;
  toggleFullScreen: () => void;
  handleDragStop: (e: any, data: { x: number; y: number }) => void;
}

/**
 * Hook centralizujący logikę zarządzania stanem chata
 * (nawigacja, tryby i open/closed)
 */
export const useChatState = (options: UseChatStateOptions = {}): UseChatStateReturn => {
  const {
    manageWindowPosition = false,
    enableKeyboardShortcuts = false
  } = options;
  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.auth);
  const {
    isAgentAiVisible,
    isChatOpen,
    isChatDocked,
    isChatHorizontal,
    isChatResizable,
    isChatFullScreen,
    isChatHeaderVisible,
    chatFloatingWidth,
    chatFloatingHeight
  } = useSelector((state) => state.chat);

  // Pobierz threadId z localStorage
  const [threadId, setThreadId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('chatThreadId') || '';
    }
    return '';
  });

  // Nasłuchuj zmian w localStorage dla chatThreadId
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'chatThreadId') {
        setThreadId(e.newValue || '');
      }
    };

    // Nasłuchuj zmian w localStorage (z innych zakładek)
    window.addEventListener('storage', handleStorageChange);

    // Sprawdzaj zmiany w localStorage w tej samej zakładce (polling)
    const interval = setInterval(() => {
      const currentThreadId = localStorage.getItem('chatThreadId') || '';
      if (currentThreadId !== threadId) {
        setThreadId(currentThreadId);
      }
    }, THREAD_ID_POLLING_INTERVAL);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [threadId]);

  // Stan pozycji okna (tylko gdy manageWindowPosition = true)
  const [chatWindowPosition, setChatWindowPositionState] = useState<ChatWindowPosition | null>(
    manageWindowPosition
      ? {
          top: window.innerHeight - chatFloatingHeight - 16,
          left: window.innerWidth - chatFloatingWidth - 16
        }
      : null
  );
  const [dragResetKey, setDragResetKey] = useState(0);

  // Memoized setter dla pozycji okna
  const setChatWindowPosition = useCallback(
    (position: ChatWindowPosition | null) => {
      if (manageWindowPosition) {
        setChatWindowPositionState(position);
      }
    },
    [manageWindowPosition]
  );

  // Keyboard shortcuts (Ctrl+A / Cmd+A)
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Sprawdź czy widget powinien być widoczny
      if (REACT_APP_IS_CHAT_WIDGET_VISIBLE?.toLowerCase() !== 'true') {
        return;
      }
      if (profile?.role === 'ROLE_OPEN_PROFILE') {
        return;
      }
      if (!isAgentAiVisible) {
        return;
      }

      // Ctrl+A (Windows/Linux) lub Cmd+A (Mac) - sprawdź zarówno 'a' jak i 'A'
      if ((event.ctrlKey || event.metaKey) && (event.key === 'a' || event.key === 'A')) {
        event.preventDefault();
        event.stopPropagation();
        const newChatOpenState = !isChatOpen;

        // Jeśli otwieramy chat po raz pierwszy, ustaw tryb horizontal jako domyślny
        if (newChatOpenState && !isChatDocked && !isChatHorizontal) {
          dispatch(reduxActions.setIsChatHorizontal(true));
        }

        dispatch(reduxActions.setIsChatOpen(newChatOpenState));
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isChatOpen, isChatDocked, isChatHorizontal, isAgentAiVisible, profile, dispatch, enableKeyboardShortcuts]);

  // Sprawdź czy widget powinien być wyświetlony
  const shouldShowAgentAI = useCallback(
    (mode?: 'docked' | 'horizontal'): boolean => {
      if (REACT_APP_IS_CHAT_WIDGET_VISIBLE?.toLowerCase() !== 'true') {
        return false;
      }

      // Widget dostępny tylko dla zalogowanych użytkowników (nie dla ROLE_OPEN_PROFILE)
      if (profile?.role === 'ROLE_OPEN_PROFILE' || !profile?.role) {
        return false;
      }

      if (!isAgentAiVisible) {
        return false;
      }

      if (mode === 'docked') {
        return isChatDocked;
      }
      if (mode === 'horizontal') {
        return isChatHorizontal;
      }
      // Jeśli nie podano trybu, zwróć true jeśli agent jest widoczny
      return true;
    },
    [isAgentAiVisible, isChatDocked, isChatHorizontal, profile?.role]
  );

  // Funkcja pomocnicza do ustawiania trybu chata
  const setChatMode = useCallback((mode: ChatMode) => {
    dispatch(reduxActions.setIsChatDocked(mode === 'docked'));
    dispatch(reduxActions.setIsChatHorizontal(mode === 'horizontal'));
    dispatch(reduxActions.setIsChatResizable(mode === 'floating'));
    dispatch(reduxActions.setChatFullScreen(mode === 'fullscreen'));
  }, [dispatch]);

  // Zarządzanie isChatHeaderVisible - włącza się tylko gdy threadId jest pusty
  useEffect(() => {
    const hasThreadId = threadId && threadId.trim() !== '';
    if (!hasThreadId && !isChatHeaderVisible) {
      dispatch(reduxActions.setIsChatHeaderVisible(true));
    }
  }, [threadId, isChatHeaderVisible, dispatch]);

  // Ustaw domyślny tryb horizontal, jeśli chat jest otwarty i nie ma żadnego trybu
  useEffect(() => {
    if (isChatOpen && !isChatDocked && !isChatHorizontal && !isChatResizable && !isChatFullScreen) {
      setChatMode('horizontal');
    }
  }, [isChatOpen, isChatDocked, isChatHorizontal, isChatResizable, isChatFullScreen, setChatMode]);

  // Zamknij chat (tryb zostanie zapamiętany w Redux i przywrócony przy otwarciu)
  const closeChat = useCallback(() => {
    dispatch(reduxActions.setIsChatOpen(false));
  }, [dispatch]);

  // Przełącz tryb docked - jeśli wyłączony, włącz
  const toggleDock = useCallback(() => {
    if (!isChatDocked) {
      setChatMode('docked');
      dispatch(reduxActions.setIsChatOpen(true));
    }
  }, [isChatDocked, setChatMode, dispatch]);

  // Przełącz tryb horizontal - jeśli wyłączony, włącz
  const toggleHorizontal = useCallback(() => {
    if (!isChatHorizontal) {
      setChatMode('horizontal');
      dispatch(reduxActions.setIsChatOpen(true));
    }
  }, [isChatHorizontal, setChatMode, dispatch]);

  // Scroll do góry (tylko dla trybu horizontal)
  const scrollToTop = useCallback(() => {
    if (!isChatHorizontal) return;
    const scrollableContent = document.getElementsByClassName('scrollableContent')[0] as HTMLElement;
    scrollableContent?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [isChatHorizontal]);

  // Przełącz chat (otwórz/zamknij)
  const toggleChat = useCallback(() => {
    const newChatOpenState = !isChatOpen;

    if (newChatOpenState && isChatHorizontal) {
      const scrollableContent = document.getElementsByClassName('scrollableContent')[0] as HTMLElement;
      if (scrollableContent?.scrollTop > 0) {
        scrollToTop();
      }
    }

    // Redux automatycznie przywróci ostatni tryb przy otwarciu (w setIsChatOpen reducerze)
    dispatch(reduxActions.setIsChatOpen(newChatOpenState));
  }, [isChatOpen, isChatHorizontal, scrollToTop, dispatch]);

  // Przełącz tryb floating (resizable)
  const toggleResizable = useCallback(() => {
    if (isChatResizable) {
      // Wyłącz floating - przełącz na horizontal
      setChatMode('horizontal');
      if (manageWindowPosition) {
        // Resetuj wysokość do wartości początkowej
        dispatch(reduxActions.setChatFloatingHeight(650));
      }
    } else {
      // Włącz floating
      setChatMode('floating');
      dispatch(reduxActions.setIsChatOpen(true));
      
      if (manageWindowPosition) {
        setChatWindowPosition({
          top: chatWindowPosition?.top ?? window.innerHeight - chatFloatingHeight - 16,
          left: chatWindowPosition?.left ?? window.innerWidth - chatFloatingWidth - 16
        });
      }
    }
  }, [isChatResizable, manageWindowPosition, chatWindowPosition, chatFloatingWidth, chatFloatingHeight, setChatMode, setChatWindowPosition, dispatch]);

  // Przełącz tryb fullscreen
  const toggleFullScreen = useCallback(() => {
    if (isChatFullScreen) {
      // Wyłączamy fullscreen - przywróć ostatni tryb (zapamiętany w reducerze)
      dispatch(reduxActions.restoreLastChatModeBeforeFullscreen());
      if (manageWindowPosition) {
        setChatWindowPosition({
          top: chatWindowPosition?.top ?? window.innerHeight - chatFloatingHeight - 16,
          left: chatWindowPosition?.left ?? window.innerWidth - chatFloatingWidth - 16
        });
      }
    } else {
      // Włączamy fullscreen
      setChatMode('fullscreen');
      dispatch(reduxActions.setIsChatOpen(true));
    }
  }, [isChatFullScreen, manageWindowPosition, chatWindowPosition, chatFloatingWidth, chatFloatingHeight, setChatMode, setChatWindowPosition, dispatch]);

  // Handler dla zakończenia przeciągania - aktualizuje pozycję okna
  const handleDragStop = useCallback(
    (e: any, data: { x: number; y: number }) => {
      if (!manageWindowPosition) return;
      if (!isChatResizable || isChatFullScreen || isChatDocked || isChatHorizontal) return;

      // Oblicz nową absolutną pozycję na podstawie początkowej pozycji i przesunięcia
      const initialTop = chatWindowPosition?.top ?? window.innerHeight - chatFloatingHeight - 16;
      const initialLeft = chatWindowPosition?.left ?? window.innerWidth - chatFloatingWidth - 16;

      const newLeft = initialLeft + data.x;
      const newTop = initialTop + data.y;

      // Ograniczenia - upewnij się, że okno nie wyjdzie poza ekran
      const maxLeft = window.innerWidth - chatFloatingWidth;
      const maxTop = window.innerHeight - chatFloatingHeight;

      setChatWindowPosition({
        left: Math.max(0, Math.min(maxLeft, newLeft)),
        top: Math.max(0, Math.min(maxTop, newTop))
      });

      // Zwiększ klucz resetu, aby wymusić remount Draggable z nową pozycją początkową
      setDragResetKey((prev) => prev + 1);
    },
    [manageWindowPosition, isChatResizable, isChatFullScreen, isChatDocked, isChatHorizontal, chatWindowPosition, chatFloatingWidth, chatFloatingHeight, setChatWindowPosition]
  );

  return {
    // Stan
    isAgentAiVisible,
    isChatOpen,
    isChatDocked,
    isChatHorizontal,
    isChatResizable,
    isChatFullScreen,

    // Stan pozycji okna
    chatWindowPosition: manageWindowPosition ? chatWindowPosition : null,
    dragResetKey: manageWindowPosition ? dragResetKey : 0,

    // Funkcje pomocnicze
    shouldShowAgentAI,

    // Funkcje zarządzania stanem
    closeChat,
    toggleChat,
    toggleDock,
    toggleHorizontal,
    toggleResizable,
    toggleFullScreen,
    handleDragStop
  };
};
